import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, combineLatest, map } from 'rxjs';
import { DEFAULT_RISK_PROFILE, DEFAULT_RISK_STEPS, RiskProfile, RiskStep } from '../models/risk-setup';

export interface RiskSummary {
  totalRiskBudgetEUR: number;
  riskPerTradeEUR: number;
  appliedStep?: RiskStep;
  positionedPnLEUR: number;
  profile: RiskProfile;
}

@Injectable({ providedIn: 'root' })
export class RiskBudgetService {
  private readonly profileSubject = new BehaviorSubject<RiskProfile>({ ...DEFAULT_RISK_PROFILE });
  private readonly positionedPnlSubject = new BehaviorSubject<number>(0);

  readonly profile$: Observable<RiskProfile> = this.profileSubject.asObservable();
  readonly positionedPnL$: Observable<number> = this.positionedPnlSubject.asObservable();

  readonly summary$: Observable<RiskSummary> = combineLatest([this.profile$, this.positionedPnL$]).pipe(
    map(([profile, positionedPnLEUR]) => this.buildSummary(profile, positionedPnLEUR))
  );

  setProfile(profile: RiskProfile): void {
    this.profileSubject.next(this.normalizeProfile(profile));
  }

  updatePositionedPnl(positionedPnLEUR: number): void {
    const clamped = isFinite(positionedPnLEUR) ? positionedPnLEUR : 0;
    this.positionedPnlSubject.next(clamped);
  }

  calculateTotalRiskBudget(maxLossThresholdEUR: number, positionedPnLEUR: number): number {
    const threshold = this.toAmount(maxLossThresholdEUR);
    const pnl = Math.max(0, this.toAmount(positionedPnLEUR));
    return this.toAmount(threshold + pnl);
  }

  calculateRiskPerTrade(positionedPnLEUR: number, baseRiskPerTradeEUR: number, riskSteps: RiskStep[]): { value: number; appliedStep?: RiskStep } {
    const base = Math.max(0, this.toAmount(baseRiskPerTradeEUR));
    const steps = this.normalizeSteps(riskSteps);
    const profitRaw = this.toAmount(positionedPnLEUR);
    if (profitRaw < 0) {
      return { value: base };
    }
    const profit = Math.max(0, profitRaw);

    let applied: RiskStep | undefined;
    for (const step of [...steps].reverse()) {
      if (profit >= step.profitMinEUR) {
        applied = step;
        break;
      }
    }

    const value = applied ? Math.max(0, this.toAmount(applied.riskPerTradeEUR)) : base;
    return { value, appliedStep: applied };
  }

  nextProfileFromForm(profile: RiskProfile): void {
    this.setProfile(profile);
  }

  private buildSummary(profile: RiskProfile, positionedPnLEUR: number): RiskSummary {
    const { value, appliedStep } = this.calculateRiskPerTrade(positionedPnLEUR, profile.baseRiskPerTradeEUR, profile.riskSteps);
    const totalRiskBudgetEUR = this.calculateTotalRiskBudget(profile.maxLossThresholdEUR, positionedPnLEUR);

    return {
      totalRiskBudgetEUR,
      riskPerTradeEUR: value,
      appliedStep,
      positionedPnLEUR: this.toAmount(positionedPnLEUR),
      profile,
    };
  }

  private normalizeProfile(profile: RiskProfile): RiskProfile {
    const normalizedSteps = this.normalizeSteps(profile.riskSteps?.length ? profile.riskSteps : DEFAULT_RISK_STEPS);
    return {
      ...profile,
      maxLossThresholdEUR: this.toAmount(profile.maxLossThresholdEUR ?? DEFAULT_RISK_PROFILE.maxLossThresholdEUR),
      baseRiskPerTradeEUR: this.toAmount(profile.baseRiskPerTradeEUR ?? DEFAULT_RISK_PROFILE.baseRiskPerTradeEUR),
      riskSteps: normalizedSteps,
      planYear: profile.planYear ?? DEFAULT_RISK_PROFILE.planYear,
      riskRewardRatio: profile.riskRewardRatio ?? DEFAULT_RISK_PROFILE.riskRewardRatio,
      winProbabilityPct: profile.winProbabilityPct ?? DEFAULT_RISK_PROFILE.winProbabilityPct,
      targetGoalEUR: profile.targetGoalEUR ?? DEFAULT_RISK_PROFILE.targetGoalEUR,
    };
  }

  private normalizeSteps(steps: RiskStep[] = []): RiskStep[] {
    return [...steps]
      .map((step) => ({
        profitMinEUR: this.toAmount(step.profitMinEUR ?? 0),
        riskPerTradeEUR: this.toAmount(step.riskPerTradeEUR ?? 0),
      }))
      .filter((step) => step.profitMinEUR >= 0 && step.riskPerTradeEUR >= 0)
      .sort((a, b) => a.profitMinEUR - b.profitMinEUR);
  }

  private toAmount(value: number): number {
    const safe = isFinite(Number(value)) ? Number(value) : 0;
    return +safe.toFixed(2);
  }
}
